import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { EmptyState } from '~/components/ui/EmptyState';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { Table, type TableColumn, type TableRow } from './Table';

export type InfiniteScrollTableProps = Readonly<{
  columns: TableColumn[];
  rows: TableRow[];
  isLoading: boolean;
  isError: boolean;
  loadingName: string;
  errorMessage: string;
  errorTestId: string;
  onRetry: () => void;
  emptyStateIcon?: ReactNode;
  emptyStateTitle: string;
  emptyStateMessage: string;
  emptyStateAction?: ReactNode;
  tableTestId: string;
  tableName: string;
  loadingMoreLabel: string;
  noMoreItemsLabel: string;
  totalCount?: number;
  isLoadingMore?: boolean;
  hasMoreData?: boolean | null;
  sentinelRef?: Ref<HTMLDivElement>;
}>;

export function InfiniteScrollTable({
  columns,
  rows,
  isLoading,
  isError,
  loadingName,
  errorMessage,
  errorTestId,
  onRetry,
  emptyStateIcon,
  emptyStateTitle,
  emptyStateMessage,
  emptyStateAction,
  tableTestId,
  tableName,
  loadingMoreLabel,
  noMoreItemsLabel,
  totalCount,
  isLoadingMore,
  hasMoreData,
  sentinelRef,
}: InfiniteScrollTableProps) {
  const { t } = useTranslation('general');

  if (isLoading) {
    return <PageLoadingSpinner name={loadingName} />;
  }

  if (isError) {
    return (
      <RetryBanner
        message={errorMessage}
        retryLabel={t('general.retry')}
        onRetry={onRetry}
        testId={errorTestId}
      />
    );
  }

  if (rows.length === 0) {
    const emptyState = (
      <EmptyState
        icon={emptyStateIcon ?? <FileText size={48} />}
        title={emptyStateTitle}
        message={emptyStateMessage}
      />
    );
    if (!emptyStateAction) return emptyState;
    return (
      <div>
        {emptyState}
        <div className="flex justify-center mt-4">{emptyStateAction}</div>
      </div>
    );
  }

  return (
    <div
      data-testid={tableTestId}
      className="rounded-lg border border-neut-border overflow-hidden"
    >
      <Table
        rows={rows}
        columns={columns}
        minWidth={64}
        totalRowsCount={totalCount}
        name={tableName}
      />
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">{loadingMoreLabel}</div>
        </div>
      )}
      {hasMoreData === false && !isLoading && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">{noMoreItemsLabel}</div>
        </div>
      )}
      <div
        ref={sentinelRef}
        id={`${tableTestId}-scroll-sentinel`}
        style={{ height: '1px', background: 'transparent' }}
        data-testid="scroll-sentinel"
      />
    </div>
  );
}
